import React, { useState, useEffect } from 'react';
import { createWhatsappSignup } from '../firebase/realtime-db';
import './WhatsappPopup.css';

const WhatsappPopup = ({ 
  isOpen, 
  onClose,
  riderLink = 'https://chat.whatsapp.com/G8Pjr1b2iri45rPhjnmDiW?mode=wwc',
  trainerLink = 'https://chat.whatsapp.com/EOQoiMJFuXL00xaSiH3xdT?mode=wwc',
  preselectedUserType = 'reiter'
}) => {
  const [userType, setUserType] = useState(preselectedUserType);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [newsletterOptIn, setNewsletterOptIn] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [showLinks, setShowLinks] = useState(false);
  const [error, setError] = useState('');

  // Check if form is valid
  const isFormValid = name.trim() && email.trim() && email.includes('@');

  const whatsappLinks = {
    reiter: riderLink,
    trainer: trainerLink
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validierung
    if (!name.trim() || !email.trim()) {
      setError('Please fill in all fields.');
      return;
    }

    if (!email.includes('@')) {
      setError('Please enter a valid email address.');
      return;
    }

    setIsLoading(true);

    try {
      // Daten mit Timestamp in Realtime Database speichern
      const userData = {
        userType,
        name: name.trim(),
        email: email.trim(),
        newsletterOptIn,
        timestamp: new Date().toISOString()
      };

      const result = await createWhatsappSignup(userData);

      if (result.error) {
        setError('Error saving data. Please try again.');
        setIsLoading(false);
        return;
      }

      // Nach erfolgreicher Speicherung die Links anzeigen
      setShowLinks(true);
      setIsLoading(false);
    } catch (err) {
      setError('An error occurred. Please try again.');
      setIsLoading(false);
    }
  };

  const handleWhatsappClick = (type) => {
    window.open(whatsappLinks[type], '_blank');
    // Optional: Modal nach Klick schließen
    setTimeout(() => {
      resetAndClose();
    }, 500);
  };

  const resetAndClose = () => {
    setUserType(preselectedUserType);
    setName('');
    setEmail('');
    setNewsletterOptIn(true);
    setShowLinks(false);
    setError('');
    onClose();
  };

  // Update userType when preselectedUserType changes
  useEffect(() => {
    if (isOpen) {
      setUserType(preselectedUserType);
    }
  }, [isOpen, preselectedUserType]);

  // Block scrolling when popup is open
  useEffect(() => {
    if (isOpen) {
      // Calculate scrollbar width
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Save current scroll position
      const scrollY = window.scrollY;
      document.body.setAttribute('data-scroll-position', scrollY.toString());
      
      // Apply styles to prevent scrolling and layout shift
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    } else {
      // Restore scroll position
      const scrollPosition = document.body.getAttribute('data-scroll-position');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.removeAttribute('data-scroll-position');
      
      if (scrollPosition) {
        window.scrollTo(0, parseInt(scrollPosition));
      }
    }

    return () => {
      // Cleanup on unmount
      const scrollPosition = document.body.getAttribute('data-scroll-position');
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.width = '';
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.removeAttribute('data-scroll-position');
      
      if (scrollPosition) {
        window.scrollTo(0, parseInt(scrollPosition));
      }
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="whatsapp-popup-overlay" onClick={resetAndClose}>
      <div className="whatsapp-popup-modal" onClick={(e) => e.stopPropagation()}>
        <div className="whatsapp-popup-seal-background">
          <img 
            src="/img/sinah_siegel_web.png" 
            alt="" 
            className="whatsapp-popup-seal-image"
            aria-hidden="true"
          />
        </div>
        <button className="whatsapp-popup-close" onClick={resetAndClose}>
          ✕
        </button>

        {!showLinks ? (
          // Formular-Phase
          <div className="whatsapp-popup-content">
            <h2 className="whatsapp-popup-title">WhatsApp Community</h2>
            <p className="whatsapp-popup-description">
              To join our WhatsApp community, please enter your information:
            </p>

            <form onSubmit={handleSubmit} className="whatsapp-popup-form">
              {/* User Type Toggle */}
              <div className="whatsapp-popup-toggle-group">
                <label className="whatsapp-popup-toggle-label">Who are you?</label>
                <div className="whatsapp-popup-toggle-buttons">
                  <button
                    type="button"
                    className={`whatsapp-popup-toggle-btn ${userType === 'reiter' ? 'active' : ''}`}
                    onClick={() => setUserType('reiter')}
                  >
                    Rider
                  </button>
                  <button
                    type="button"
                    className={`whatsapp-popup-toggle-btn ${userType === 'trainer' ? 'active' : ''}`}
                    onClick={() => setUserType('trainer')}
                  >
                    Trainer
                  </button>
                </div>
              </div>

              {/* Name Input */}
              <div className="whatsapp-popup-form-group">
                <label htmlFor="whatsapp-name" className="whatsapp-popup-label">
                  Name
                </label>
                <input
                  id="whatsapp-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Enter your name"
                  className="whatsapp-popup-input"
                  disabled={isLoading}
                />
              </div>

              {/* Email Input */}
              <div className="whatsapp-popup-form-group">
                <label htmlFor="whatsapp-email" className="whatsapp-popup-label">
                  Email
                </label>
                <input
                  id="whatsapp-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  className="whatsapp-popup-input"
                  disabled={isLoading}
                />
              </div>

              {/* Newsletter Checkbox */}
              <div className="whatsapp-popup-checkbox-group">
                <label className="whatsapp-popup-checkbox-label">
                  <input
                    type="checkbox"
                    checked={newsletterOptIn}
                    onChange={(e) => setNewsletterOptIn(e.target.checked)}
                    className="whatsapp-popup-checkbox-input"
                    disabled={isLoading}
                  />
                  <span className="whatsapp-popup-checkbox-custom"></span>
                  <span className="whatsapp-popup-checkbox-text">
                    I want to receive valuable tips and updates via email regularly
                  </span>
                </label>
              </div>

              {/* Error Message */}
              {error && <div className="whatsapp-popup-error">{error}</div>}

              {/* Submit Button */}
              <button
                type="submit"
                className={`whatsapp-popup-submit-btn ${!isFormValid ? 'inactive' : ''}`}
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Continue'}
              </button>
            </form>
          </div>
        ) : (
          // Links-Phase
          <div className="whatsapp-popup-links-content">
            <h2 className="whatsapp-popup-title">WhatsApp Group</h2>
            <p className="whatsapp-popup-description">
              Welcome, {name}! Click below to join the WhatsApp group for {userType === 'reiter' ? 'Riders' : 'Trainers'}:
            </p>

            <div className="whatsapp-popup-buttons-group">
              {userType === 'reiter' && (
                <button
                  className="whatsapp-popup-link-btn"
                  onClick={() => handleWhatsappClick('reiter')}
                >
                  <img src="/img/whatsapp.svg" alt="WhatsApp" className="whatsapp-popup-icon" />
                  Join Riders Group
                </button>
              )}

              {userType === 'trainer' && (
                <button
                  className="whatsapp-popup-link-btn"
                  onClick={() => handleWhatsappClick('trainer')}
                >
                  <img src="/img/whatsapp.svg" alt="WhatsApp" className="whatsapp-popup-icon" />
                  Join Trainers Group
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappPopup;
